import useAuth from '../models/useAuth'
import { accessCheckURL, accessRequestURL } from '../utilities/serviceName'

const routerApiBaseUrl =
  import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

export type AccessCheckResult = {
  authorized: boolean
  serviceId: string | null
  serviceTitle: string
  email: string
}

type AccessCheckRow = {
  found: boolean
  // The service id scopes useAccessRequestWatcher's realtime events to this
  // service. A router that does not send it leaves the watcher unscoped.
  id?: string
  name?: string
  title?: string
  authorized?: boolean
}

// Asks the ROUTER, which answers with the same predicate its service proxy
// enforces (nobgp/nobgp-router#339: `GET /api/access/{name}`, `db.Service.Admits`).
// It used to ask a SQL function, `check_service_access`, which authorized the
// network owner while the proxy authorized org members — so this page could send
// a visitor back to a proxy that refused them. That function is dropped.
export async function checkAccess(serviceName: string): Promise<AccessCheckResult | null> {
  const email = useAuth.getState().user?.email ?? ''
  // A name that is not a DNS label is not a service: no fetch (app#377).
  const url = accessCheckURL(routerApiBaseUrl, serviceName)
  if (!url) return null

  try {
    const response = await fetch(url, {
      credentials: 'include',
    })
    if (!response.ok) return null

    const row = (await response.json()) as AccessCheckRow
    if (!row.found) return null

    return {
      authorized: row.authorized === true,
      serviceId: row.id ?? null,
      serviceTitle: row.title || row.name || serviceName,
      email,
    }
  } catch {
    return null
  }
}

// `rejected` is an answer, not a failure: the owner already turned this requester down and the router
// declines to re-ask on their behalf until the rejection lapses.
export type AccessRequestResult = { ok: boolean; status?: 'pending' | 'rejected'; error?: string }

export async function requestAccess(serviceName: string): Promise<AccessRequestResult> {
  // The page POSTs on arrival, from a link anybody can make: a name that is not a
  // DNS label must not reach the URL (app#377, router#1768 RP-21).
  const url = accessRequestURL(routerApiBaseUrl, serviceName)
  if (!url) return { ok: false, error: 'This is not a valid service name.' }

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
    })

    if (!response.ok) {
      const text = await response.text()
      return { ok: false, error: text || 'Request failed' }
    }

    // A router that predates the rejection state answers `{ok: true}` and nothing else; an absent
    // status is pending, which is what that router meant.
    const body = (await response.json().catch(() => ({}))) as { status?: string }
    return { ok: true, status: body.status === 'rejected' ? 'rejected' : 'pending' }
  } catch {
    return { ok: false, error: 'Unable to send request' }
  }
}
